import { ApolloLink, Observable } from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';

/**
 * When a record's save is rejected because required fields are empty (wfc-server's
 * RequiredFieldsMissingError: code REQUIRED_FIELDS_MISSING, the fields in extensions.details),
 * this asks for just those fields (RequiredFieldsModal) and resends the same mutation with them
 * added to its `properties`. Whatever made the save - a detail page, the pay rate modal, the
 * sidebar - gets the result as if it had sent them itself, so none of them handle this on its own.
 * If the user cancels, the original error goes through as before.
 */

// RequiredFieldsModal (mounted once in DefaultLayout) registers itself here.
let openPrompt = null;

export const registerRequiredFieldsPrompt = (prompt) => {
    openPrompt = prompt;
    return () => { if (openPrompt === prompt) openPrompt = null; };
};

// Resolves with the filled-in properties entries, or null when cancelled.
const askForRequiredFields = (details) => (openPrompt ? openPrompt(details) : Promise.resolve(null));

// The update mutations take their change-list as {input: {_id, properties: [...]}}.
const propertiesKey = (variables) =>
    Object.keys(variables || {}).find((key) => Array.isArray(variables[key]?.properties));

export const requiredFieldsLink = new ApolloLink((operation, forward) => {
    const definition = getMainDefinition(operation.query);
    if (definition.kind !== 'OperationDefinition' || definition.operation !== 'mutation') {
        return forward(operation);
    }

    return new Observable((observer) => {
        let subscription;
        let asking = false;

        const send = () => {
            subscription = forward(operation).subscribe({
                next: (result) => {
                    const error = result?.errors?.find((e) => e?.extensions?.code === 'REQUIRED_FIELDS_MISSING');
                    const key = error && propertiesKey(operation.variables);
                    if (!key || !error?.extensions?.details?.fields?.length) {
                        observer.next(result);
                        return;
                    }

                    asking = true;
                    askForRequiredFields(error.extensions.details).then((filled) => {
                        asking = false;
                        if (!filled?.length) {
                            observer.next(result);
                            observer.complete();
                            return;
                        }
                        const names = new Set(filled.map((entry) => entry.name));
                        const input = operation.variables[key];
                        operation.variables = {
                            ...operation.variables,
                            [key]: { ...input, properties: [...input.properties.filter((entry) => !names.has(entry?.name)), ...filled] },
                        };
                        send();
                    });
                },
                error: (err) => observer.error(err),
                // held back while asking - the resent mutation completes instead
                complete: () => { if (!asking) observer.complete(); },
            });
        };

        send();
        return () => subscription?.unsubscribe();
    });
});
